import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { cn } from "@/lib/utils";
import { Color } from "chart.js";
import React, { useCallback } from "react";

export interface LegendItem {
  text: string;
  fillStyle?: Color | undefined;
  hidden: boolean;
  lineWidth?: number;
  strokeStyle?: string;
  datasetIndex: number;
  reportName?: string;
  datasetId?: string;
  datasetName?: string;
}

interface CustomLegendProps {
  legendItems: LegendItem[];
  onLegendItemClick: (index: number) => void;
  displayOnlyDatasetName?: boolean;
}

const CustomLegend: React.FC<CustomLegendProps> = ({
  legendItems,
  onLegendItemClick,
  displayOnlyDatasetName,
}) => {
  const handleClick = useCallback(
    (index: number) => {
      onLegendItemClick(index);
    },
    [onLegendItemClick],
  );

  if (!legendItems.length) {
    return null;
  }

  return (
    <div className="flex flex-col py-2">
      <div className="mb-1 text-xs font-medium text-gray-500">
        {displayOnlyDatasetName ? "Datasets" : "Pipelines"}
      </div>
      <div className="flex flex-nowrap items-center justify-start gap-4 overflow-x-auto">
        {legendItems.map((item, index) => (
          <div
            key={`${item.datasetId}-${index}`}
            className={cn(
              "flex cursor-pointer select-none items-center gap-2 rounded-md px-2 py-1 text-xs font-medium transition-colors hover:bg-gray-100",
              item.hidden && "opacity-50",
            )}
            onClick={() => handleClick(item.datasetIndex)}
            title={`${item.reportName || ""} - ${item.datasetName || ""}`}
          >
            <div
              className="h-3 w-3 rounded-full"
              style={{
                backgroundColor: item.fillStyle?.toString(),
                border: `1px solid ${item.strokeStyle || item.fillStyle}`,
              }}
            />
            <div className="flex max-w-[150px] flex-col ">
              {!displayOnlyDatasetName ? (
                <OverflowDisplayTooltip
                  content={item.reportName || item.text.split(" - ")[0]}
                >
                  <span className="font-semibold">
                    {item.reportName || item.text.split(" - ")[0]}
                  </span>
                </OverflowDisplayTooltip>
              ) : null}
              {item.datasetId && (
                <OverflowDisplayTooltip content={item.datasetName}>
                  <span
                    className={cn(
                      displayOnlyDatasetName
                        ? "font-semibold text-gray-900 "
                        : "text-[10px] text-gray-500",
                    )}
                  >
                    {!displayOnlyDatasetName
                      ? `Dataset: ${item.datasetName}`
                      : `${item.datasetName}`}
                  </span>
                </OverflowDisplayTooltip>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default React.memo(CustomLegend);
